/**
 * A data-provider credit that has to stay painted on the map while that provider's data is drawn.
 *
 * Most of our maps need nothing here: they keep Mapbox GL JS's default `AttributionControl`, which
 * already renders the `attribution` string each loaded source declares — and the served Lidar style
 * declares one on its `mapterhorn-dem` source. This exists for the maps that switch that control
 * off (Explore, the hike embed, compare maps, the elevation preview), where Mapterhorn's terms are
 * met by nothing else on screen.
 *
 * The credit is derived from the *loaded style's own sources*, the same input GL JS's control reads,
 * rather than from the selected style's name. Base styles get swapped through several different code
 * paths on web — the styles dialog, the Vuex module, print mode, and two components that clone
 * `mainMap.getStyle()` — so reading the style that actually ended up on the map is the only check
 * that can't be bypassed by a path nobody remembered to update.
 */
export interface MapDataCredit {
  label: string;
  attributionUrl: string;
}

/**
 * Matches the wording Mapterhorn's own tile source declares in the served style JSON, so the pill
 * and the standard attribution strip read identically. Kept in step with iOS's and Android's
 * `MapDataCredit.mapterhorn`.
 */
export const MAPTERHORN_CREDIT: MapDataCredit = {
  label: '© Mapterhorn',
  attributionUrl: 'https://mapterhorn.com/attribution',
};

/** Substring we look for in a source's `attribution` HTML. */
const MAPTERHORN_MARKER = 'mapterhorn';

/**
 * The one field we care about from a style spec's `sources` map. The index signature is what lets a
 * real source spec — `type`, `tiles`, `url`, `encoding` and the rest — be passed straight in, rather
 * than tripping TypeScript's weak-type check for having nothing in common with a lone optional.
 */
export interface StyleSourceLike {
  attribution?: string;
  [key: string]: unknown;
}

/**
 * The credits owed by a loaded style, keyed off what its sources declare.
 *
 * Mapbox and OpenStreetMap are deliberately not returned: on the surfaces that keep the default
 * control they are already credited there, and on the surfaces that don't, their own branding is a
 * separate decision from Mapterhorn's licence.
 *
 * A source is credited whenever it declares a Mapterhorn attribution, without checking whether a
 * layer currently draws from it. Over-crediting a style that ships the source but never paints it
 * is harmless; under-crediting one is a licence problem.
 */
export function mapDataCreditsForSources(
  sources: Record<string, StyleSourceLike | null | undefined> | null | undefined,
): MapDataCredit[] {
  if (!sources) return [];

  const creditsMapterhorn = Object.values(sources).some((source) =>
    (source?.attribution ?? '').toLowerCase().includes(MAPTERHORN_MARKER),
  );

  return creditsMapterhorn ? [MAPTERHORN_CREDIT] : [];
}
